import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { useLocale } from "@/i18n";
import {
  CURRENCY_COOKIE,
  DEFAULT_CURRENCY,
  FALLBACK_RATES,
  currencyForCountry,
  currencyForLocale,
  currencyFromTimeZone,
  isCurrencyCode,
  type CurrencyCode,
} from "./config";

type CurrencyContextValue = {
  currency: CurrencyCode;
  setCurrency: (next: CurrencyCode) => void;
};

const CurrencyContext = createContext<CurrencyContextValue>({
  currency: DEFAULT_CURRENCY,
  setCurrency: () => {},
});

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [k, ...v] = part.trim().split("=");
    if (k === name) return decodeURIComponent(v.join("="));
  }
  return null;
}

function persistCurrency(code: CurrencyCode) {
  if (typeof document === "undefined") return;
  document.cookie = `${CURRENCY_COOKIE}=${code}; path=/; max-age=31536000; samesite=lax`;
  try {
    window.localStorage.setItem(CURRENCY_COOKIE, code);
  } catch {
    /* storage unavailable (private mode) */
  }
}

/** Auto-detects a currency from stored preference, then locale + timezone/country hints. */
function detectCurrency(locale: string): CurrencyCode {
  if (typeof window === "undefined") return currencyForLocale(locale);
  const stored = readCookie(CURRENCY_COOKIE) ?? window.localStorage.getItem(CURRENCY_COOKIE);
  if (isCurrencyCode(stored)) return stored;

  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions();
    const fromTz = currencyFromTimeZone(resolved.timeZone);
    if (fromTz) return fromTz;
  } catch {
    /* Intl unsupported */
  }

  try {
    const region = new Intl.Locale(navigator.language).maximize().region;
    const fromCountry = currencyForCountry(region ?? undefined);
    if (fromCountry) return fromCountry;
  } catch {
    /* Intl.Locale unsupported */
  }

  return currencyForLocale(locale);
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const [currency, setCurrencyState] = useState<CurrencyCode>(DEFAULT_CURRENCY);

  useEffect(() => {
    setCurrencyState(detectCurrency(locale));
    // Only run auto-detection once on mount — manual overrides should stick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCurrency = useCallback((next: CurrencyCode) => {
    setCurrencyState(next);
    persistCurrency(next);
  }, []);

  const value = useMemo(() => ({ currency, setCurrency }), [currency, setCurrency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrencyContext(): CurrencyContextValue {
  return useContext(CurrencyContext);
}

export { FALLBACK_RATES };
